import { t } from "i18next";
import { makeAutoObservable } from "mobx";
import { appStore } from "src/appStore";
import { AnchorItem } from "src/common/anchorItem";
import { loadObject } from "src/common/api/apiObject";
import { apiExtRegUrl } from "src/common/apiUrl";
import { loadAttrTypeMap } from "src/common/attributes";
import { getValidStateId } from "src/common/getValidStateId";
import { RemoteData } from "src/common/RemoteData";
import { rest } from "src/common/rest";
import {
  FormBlockDef,
  FormWithBlockStore,
} from "src/components/FormWithBlocks";
import { ZObjectItem } from "src/types/ZObjectItem";
import { z } from "zod";
import { zUid, ZUid } from "src/types/zUid";
import { BuilderCtx } from "../EntityCardPage/blockBuilder/BuilderCtx";
import { buildMainBlock } from "../EntityCardPage/blockBuilder/buildMainBlock";
import { anchorList } from "../EntityCardPage/EntityFormWithAnchor/anchorList";
import { zRegInfo, ZRegInfo, zRegSuccessMessage } from "./Register.types";

interface RegData {
  regInfo: ZRegInfo;
  objInfo: ZObjectItem;
  typesMap: Record<number, string>;
}

type RegState = "finish" | "input";

export const registerPageStore = makeAutoObservable({
  data: { status: "none" } as RemoteData<RegData>,
  setData(newData: RemoteData<RegData>) {
    this.data = newData;
  },
  state: "input" as RegState,
  setState(s: RegState) {
    this.state = s;
  },
  settingsId: null as ZUid | null,
  setSettingsId(id: ZUid | null) {
    this.settingsId = id;
  },
  successMessage: null as string | null,
  setSuccessMessage(message: string) {
    this.successMessage = message;
  },
  async init(param: string) {
    try {
      this.setState("input");
      this.setData({ status: "wait" });
      const resp1 = await rest.get(apiExtRegUrl(`/registration/${param}`), {
        translate: true,
      });
      const regInfo = zRegInfo.parse(resp1.data);

      this.setSettingsId(regInfo.settingsId);
      // Другие запросы к сервисам можно делать только после этой строки, т.к. иначе не будет правильного проксирования урлов

      const objInfo = await loadObject(regInfo.objectId, {
        stateId: getValidStateId(regInfo.settings.stateId),
        translate: true,
      });
      const typesMap = await loadAttrTypeMap();

      this.setData({ status: "ready", result: { regInfo, objInfo, typesMap } });
    } catch (error) {
      this.setData({ status: "error", error });
    }
  },
  formStore: new FormWithBlockStore(),
  get block(): FormBlockDef | undefined {
    if (this.data.status !== "ready") return undefined;
    const { objInfo, typesMap, regInfo } = this.data.result;
    const ctx: BuilderCtx = {
      canUpdate: true,
      typesMap,
      stateId: regInfo.settings.stateId,
      objectId: String(objInfo.id),
    };
    return buildMainBlock(objInfo, ctx);
  },
  get anchors(): AnchorItem[] {
    return anchorList(this.block);
  },
  get header(): string {
    const obj: string =
      this.data.status === "ready" ? this.data.result.objInfo.name : "---";
    return t("Registration of object", { obj });
  },
  get instuctionLink(): string {
    return appStore.curLang === "ru"
      ? "https://omnd.sharepoint.com/:w:/s/OmnidataPLM/EST5T1wK8_lFp9xxD5DAP40BzAitqF6PsaRTlp00DfeJ_Q?rtime=kqtSoOfL3Eg"
      : "https://omnd.sharepoint.com/:w:/s/OmnidataPLM/ERsMYL2oAiFJgC_emTWpxuYBC5eQYbAbxaauFYYx4u5ruQ";
  },
  async submit(formValues: unknown) {
    if (this.data.status !== "ready") {
      throw Error("Data are not ready");
    }
    const { settingsId } = this.data.result.regInfo;
    const zValues = z.record(zUid, z.string().array().optional());
    const validValues = zValues.parse(formValues);
    const data = {
      settingsId,
      attributeValues: Object.entries(validValues).map(([key, values]) => ({
        attributeId: key,
        values,
      })),
    };
    const resp = await rest.post(apiExtRegUrl("/registration"), data, {
      translate: true,
    });
    const { success, message } = zRegSuccessMessage.parse(resp.data);
    if (success) this.setSuccessMessage(message);
    return formValues;
  },
});

export type RegisterPageStore = typeof registerPageStore;
